import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";

import Badge from "../../ui/badge/Badge";

import { Edit, Palette, Trash2, Star } from 'lucide-react';
import { useNavigate, useParams } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import MoreButton from '../../common/MoreButton';
import Button from "../../ui/button/Button";
import Modal from '../../common/Modal';
import Alert from '../../ui/alert/Alert';

// Definir os tipos necessários
type AlertType = "success" | "error" | "warning" | "info";

// Definição da interface do checkout
interface Checkout {
    id: string;
    checkoutId: string;
    offerId: string;
    name: string;
    description: string;
    status: string;
    type: string;
    active: boolean;
    createdAt: string;
    updatedAt: string;
    offer?: {
        name: string;
        price: string;
    };
    slug: string;
}

interface CheckoutOfferTableProps {
    productId?: string;
}

export default function CheckoutOfferTable({ productId }: CheckoutOfferTableProps) {
    const navigate = useNavigate();
    const { id: paramProductId } = useParams<{ id: string }>();
    const productIdToUse = productId || paramProductId;

    const [checkouts, setCheckouts] = useState<Checkout[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: AlertType;
        message: string;
        title: string;
    } | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [checkoutToDelete, setCheckoutToDelete] = useState<{ id: string, offerId: string } | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchCheckouts = async () => {
            try {
                setLoading(true);
                setError(null);

                // Buscar todos os checkouts do produto usando a nova rota
                const response = await api.get(`/api/products/${productIdToUse}/checkouts`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data) {
                    // Filtrar checkouts para não exibir aqueles com status "deleted"
                    const filteredCheckouts = response.data.filter((checkout: Checkout) =>
                        checkout.status !== "deleted"
                    );
                    setCheckouts(filteredCheckouts);
                } else {
                    setCheckouts([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar checkouts do produto:", err);
                setError(err.message || "Erro ao carregar checkouts do produto");
            } finally {
                setLoading(false);
            }
        };

        if (productIdToUse) {
            fetchCheckouts();
        }
    }, [productIdToUse]);

    // Função para editar um checkout
    const handleEditCheckout = (checkoutId: string, offerId: string) => {
        navigate(`/products/${productIdToUse}/edit-checkout/${checkoutId}?offerId=${offerId}`);
    };

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = checkouts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(checkouts.length / itemsPerPage);

    const handleDeleteCheckout = async () => {
        if (!checkoutToDelete) return;

        setDeleteLoading(true);
        try {
            await api.delete(
                `/api/products/${productIdToUse}/offers/${checkoutToDelete.offerId}/checkouts/${checkoutToDelete.id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Fechar o modal e limpar o estado
            setDeleteModalOpen(false);
            setCheckoutToDelete(null);
            setDeleteLoading(false);

            // Recarregar os dados
            const fetchCheckouts = async () => {
                try {
                    setLoading(true);
                    setError(null);

                    const response = await api.get(`/api/products/${productIdToUse}/checkouts`, {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    });

                    if (response.data) {
                        // Filtrar checkouts para não exibir aqueles com status "deleted"
                        const filteredCheckouts = response.data.filter((checkout: Checkout) =>
                            checkout.status !== "deleted"
                        );
                        setCheckouts(filteredCheckouts);
                    } else {
                        setCheckouts([]);
                    }
                } catch (err: any) {
                    console.error("Erro ao buscar checkouts do produto:", err);
                    setError(err.message || "Erro ao carregar checkouts do produto");
                } finally {
                    setLoading(false);
                }
            };
            fetchCheckouts();

            // Mostrar mensagem de sucesso
            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Checkout excluído com sucesso."
            });

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        } catch (error) {
            console.error("Erro ao excluir checkout:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao excluir checkout. Tente novamente."
            });
            setDeleteLoading(false);
        }
    };

    const openDeleteModal = (checkoutId: string, offerId: string) => {
        setCheckoutToDelete({ id: checkoutId, offerId });
        setDeleteModalOpen(true);
    };

    // Função para definir checkout como padrão
    const handleSetAsDefault = async (checkoutId: string) => {
        try {
            await api.put(
                `/api/checkout-builder/${checkoutId}/set-default`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Recarregar os dados
            const response = await api.get(`/api/products/${productIdToUse}/checkouts`, {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });

            if (response.data) {
                const filteredCheckouts = response.data.filter((checkout: Checkout) =>
                    checkout.status !== "deleted"
                );
                setCheckouts(filteredCheckouts);
            }

            // Mostrar mensagem de sucesso
            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Checkout definido como padrão com sucesso."
            });

            setTimeout(() => setAlertInfo(null), 3000);
        } catch (error) {
            console.error("Erro ao definir checkout como padrão:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao definir checkout como padrão. Tente novamente."
            });
        }
    };

    if (loading) {
        return <div className="p-4 text-center">Carregando checkouts...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    if (checkouts.length === 0) {
        return (
            <ComponentCard title="Checkouts">
                <div className="p-8 text-center text-gray-500">
                    Este produto ainda não possui checkouts.
                </div>
                <div className="flex justify-center pb-4">
                    <Button
                        size="md"
                        variant="primary"
                        onClick={() => navigate(`/products/${productId}/new-checkout`)}
                    >
                        Novo Checkout
                    </Button>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Personalização de Checkout">
            {alertInfo && alertInfo.show && (
                <Alert
                    variant={alertInfo.type}
                    title={alertInfo.title}
                    message={alertInfo.message}
                />
            )}
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Campo de busca - com largura limitada */}
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar checkout..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>

                        {/* Botão - diretamente ao lado do campo de busca */}
                        <Button
                            size="md"
                            variant="primary"
                            disabled={loading}
                            onClick={() => navigate(`/products/${productIdToUse}/new-checkout`)}
                        >
                            Novo Checkout
                        </Button>
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        {/* Table Header */}
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Nome
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Descrição
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Ofertas
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400"
                                >
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        {/* Table Body */}
                        <TableBody>
                            {currentItems.map((checkout) => (
                                <TableRow
                                    key={checkout.id}
                                    className="border-b border-gray-100 last:border-b-0 dark:border-white/[0.05]"
                                >
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {checkout.name}
                                                {checkout.type === 'default' && (
                                                    <span className="inline-block ms-3 mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full dark:bg-blue-900/30 dark:text-blue-400">
                                                        Padrão
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                                {checkout.description}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-start dark:text-white/80">
                                        <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                            {checkout.offer?.name || "Todas as ofertas"}
                                        </span>
                                    </TableCell>

                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={checkout.active ? "success" : "error"}
                                        >
                                            {checkout.active ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-end dark:text-white/80">
                                        <div className="flex justify-end gap-2 relative">
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Editar",
                                                        onClick: () => handleEditCheckout(checkout.id, checkout.offerId),
                                                        icon: <Edit className="size-4" />
                                                    },
                                                    {
                                                        label: "Personalizar",
                                                        onClick: () => window.open(`/checkout-builder/${checkout.id}`, '_blank'),
                                                        icon: <Palette className="size-4" />
                                                    },
                                                    // Mostrar "Definir como padrão" apenas se NÃO for padrão
                                                    ...(checkout.type !== 'default' ? [{
                                                        label: "Definir como padrão",
                                                        onClick: () => handleSetAsDefault(checkout.id),
                                                        icon: <Star className="size-4" />,
                                                        className: "text-yellow-600 dark:text-yellow-400"
                                                    }] : []),
                                                    // Mostrar "Excluir" apenas se NÃO for padrão
                                                    ...(checkout.type !== 'default' ? [{
                                                        label: "Excluir",
                                                        onClick: () => openDeleteModal(checkout.id, checkout.offerId),
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }] : [])
                                                ]}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
                <div className="mt-4 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            {/* Modal de confirmação de exclusão */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setCheckoutToDelete(null);
                }}
                title="Confirmar exclusão"
                size="sm"
            >
                <div className="p-6">
                    <p className="text-gray-700 dark:text-gray-300">
                        Tem certeza que deseja excluir este checkout? Esta ação não pode ser desfeita.
                    </p>
                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            variant="outline"
                            disabled={deleteLoading}
                            onClick={() => {
                                setDeleteModalOpen(false);
                                setCheckoutToDelete(null);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleDeleteCheckout}
                            disabled={deleteLoading}
                            className="text-red-500 border-red-500 hover:bg-red-50"
                        >
                            Excluir
                        </Button>
                    </div>
                </div>
            </Modal>
        </ComponentCard>
    );
}